/**
 * Generates UPC/GTIN variants to support different POS systems that use various formats.
 * For input "028400009850", generates: original value, 12-digit (028400009850), 11-digit without leading zero (28400009850),
 * 11-digit without check digit (02840000985), 10-digit compact (2840000985), and GTIN-14 (00028400009850).
 * Rejects non-numeric input, codes with <10 digits, 13 digits, or >14 digits.
 */

export type UpcVariantResult = {
  variants: string[];
  errors: string[];
};

const isNumeric = (str: string) => /^\d+$/.test(str);

const padLeft = (str: string, length: number) => str.padStart(length, '0');

export const generateUpcVariants = (upcs: string[]): UpcVariantResult => {
  const variants = new Set<string>();
  const errors: string[] = [];

  upcs.forEach((upc) => {
    const trimmed = upc.trim();

    // Rejects: non-numeric, <10 digits, 13 digits, or >14 digits
    if (
      !isNumeric(trimmed) ||
      trimmed.length < 10 ||
      trimmed.length > 14 ||
      trimmed.length === 13
    ) {
      errors.push('Please enter a valid UPC to generate variants.');
      return;
    }

    variants.add(trimmed);

    // Generates: 12-digit (padded or trimmed), 11-digit without leading 0, 11-digit without check digit,
    // 10-digit compact (if leading 0), and GTIN-14 (padded to 14 digits)
    // For 14-digit input, take first 12 digits; otherwise pad to 12
    const upc12 = trimmed.length === 14 ? trimmed.slice(0, 12) : padLeft(trimmed, 12);
    variants.add(upc12);

    if (upc12.startsWith('0')) {
      variants.add(upc12.slice(1)); // 11-digit: remove leading 0
    }

    variants.add(upc12.slice(0, 11)); // 11-digit: remove check digit

    if (upc12.startsWith('0')) {
      variants.add(upc12.slice(1, 11)); // 10-digit: remove leading 0 and check digit
    }

    const gtin14 = padLeft(trimmed, 14);
    variants.add(gtin14); // GTIN-14: padded to 14 digits
  });

  return {
    variants: Array.from(variants),
    errors,
  };
};
